/*
 * 作者: 吴松泽
 * 创建时间: 2019-04-02 17:04:21
 * 修改时间: 2022-05-06 14:48:04
 * 最后修改: 吴松泽
 * 版本: [1.1]
 * 版权: 国泰新点软件股份有限公司
 * 描述: 移值 M7 框架中的 Util.charset
 */

/**
 * UTF16和UTF8转换对照表
 * UTF16为双字节  但是 UTF8为变长字节
 * U+00000000 – U+0000007F   0xxxxxxx
 * U+00000080 – U+000007FF   110xxxxx 10xxxxxx
 * U+00000800 – U+0000FFFF   1110xxxx 10xxxxxx 10xxxxxx
 * U+00010000 – U+001FFFFF   11110xxx 10xxxxxx 10xxxxxx 10xxxxxx
 * U+00200000 – U+03FFFFFF   111110xx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
 * U+04000000 – U+7FFFFFFF   1111110x 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
 */
/**
 * 常用js运算
 * js的 charAt可返回指定位置的字符。
 * charCodeAt 方法可返回指定位置的字符的 Unicode 编码
 * fromCharCode 可接受一个指定的 Unicode 值，然后返回一个字符串。
 * | 为按位或
 * & 为按位与
 * >> 为右移运算,右移一位等于除2
 * 注意一点:外部的设置charset只是设置js显示的编码格式
 * 也就是说在不同编辑器中的字显示的编码格式,但js内部运行用的统一是utf16
 */
const charset = {
    /**
     * 将字符串转为二进制(用数组装,但是最终也转为字符串输出)
     * 每一个字符均转为一个8位二进制
     * @param {String} str 目标字符串,必须是在Ascii码范围内
     * @return {String} 输出的目标二进制字符串
     */
    strToBinary(str) {
        const res = [];

        if (str) {
            const len = str.length;

            for (let i = 0; i < len; i++) {
                // 由于传入字符串是Ascii码范围,所以获得的是Ascii码
                const code = str.charCodeAt(i);
                // 将Ascii码转为二进制
                let binaryStr = parseInt(code, 10).toString(2);
                // 需要补齐8位
                const paddingLen = 8 - binaryStr.length;

                for (let j = 0; j < paddingLen; j++) {
                    binaryStr = `0${binaryStr}`;
                }
                res.push(binaryStr);
            }
        }

        return res.join('');
    },

    /**
     * 将utf-16编码字符串转为utf-8编码字符串
     * @param {String} str 传入的 utf16编码字符串(javascript内置的就是utf16编码)
     * @return {String} utf8编码的字符串,js打印会有乱码
     */
    utf16ToUtf8(str) {
        if (!str) {
            // ''字符属于ascii码,所以不必担心不同编码的转换问题
            return '';
        }
        // res是用来存放结果的字符数组,最终会转为字符串返回
        const res = [];
        const len = str.length;

        for (let i = 0; i < len; i++) {
            const code = str.charCodeAt(i);

            let byte1;
            let byte2;
            let byte3;
            let byte4;
            let byte5;
            let byte6;

            if (code > 0x0000 && code <= 0x007f) {
                // 单字节，这里并不考虑0x0000，因为它是空字节
                // U+00000000 – U+0000007F  0xxxxxxx
                res.push(str.charAt(i));
            } else if (code >= 0x0080 && code <= 0x07ff) {
                // 双字节
                // U+00000080 – U+000007FF 	110xxxxx 10xxxxxx
                // 110xxxxx
                byte1 = 0xc0 | ((code >> 6) & 0x1f);
                // 10xxxxxx
                byte2 = 0x80 | (code & 0x3f);
                res.push(String.fromCharCode(byte1), String.fromCharCode(byte2));
            } else if (code >= 0x0800 && code <= 0xffff) {
                // 三字节
                // U+00000800 – U+0000FFFF  1110xxxx 10xxxxxx 10xxxxxx
                // 1110xxxx
                // 0xE0 为224 二进制为 11100000
                // 同理,需要留下 12位给低位
                // 0x0F为15 00001111
                byte1 = 0xe0 | ((code >> 12) & 0x0f);
                // 10xxxxxx
                // 再留6位给低位
                byte2 = 0x80 | ((code >> 6) & 0x3f);
                // 10xxxxxx
                byte3 = 0x80 | (code & 0x3f);

                res.push(String.fromCharCode(byte1), String.fromCharCode(byte2), String.fromCharCode(byte3));
            } else if (code >= 0x00010000 && code <= 0x001fffff) {
                // 四字节
                // U+00010000 – U+001FFFFF  11110xxx 10xxxxxx 10xxxxxx 10xxxxxx
                // 同理,需要留下 18位给低位
                // 0x07 00000111
                // 0xF0  240 11110000
                byte1 = 0xf0 | ((code >> 18) & 0x07);
                // 10xxxxxx
                // 再留12位给低位
                byte2 = 0x80 | ((code >> 12) & 0x3f);
                // 再留6位给低位
                byte3 = 0x80 | ((code >> 6) & 0x3f);
                // 10xxxxxx
                byte4 = 0x80 | (code & 0x3f);

                res.push(
                    String.fromCharCode(byte1),
                    String.fromCharCode(byte2),
                    String.fromCharCode(byte3),
                    String.fromCharCode(byte4)
                );
            } else if (code >= 0x00200000 && code <= 0x03ffffff) {
                // 五字节
                // U+00200000 – U+03FFFFFF  111110xx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
                // 同理,需要留下 24位给低位
                // 0x03 00000011
                // 0xF8  248 11111000
                byte1 = 0xf8 | ((code >> 24) & 0x03);
                // 10xxxxxx
                // 再留18位给低位
                byte2 = 0x80 | ((code >> 18) & 0x3f);
                // 再留12位给低位
                byte3 = 0x80 | ((code >> 12) & 0x3f);
                // 再留6位给低位
                byte4 = 0x80 | ((code >> 6) & 0x3f);
                // 10xxxxxx
                byte5 = 0x80 | (code & 0x3f);

                res.push(
                    String.fromCharCode(byte1),
                    String.fromCharCode(byte2),
                    String.fromCharCode(byte3),
                    String.fromCharCode(byte4),
                    String.fromCharCode(byte5)
                );
            } else {
                // if (code >= 0x04000000 && code <= 0x7FFFFFFF)
                // 六字节
                // U+04000000 – U+7FFFFFFF  1111110x 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
                // 同理,需要留下 24位给低位
                // 0x01 00000001
                // 0xFC  252 11111100
                byte1 = 0xfc | ((code >> 30) & 0x01);
                // 10xxxxxx
                // 再留24位给低位
                byte2 = 0x80 | ((code >> 24) & 0x3f);
                // 再留18位给低位
                byte3 = 0x80 | ((code >> 18) & 0x3f);
                // 再留12位给低位
                byte4 = 0x80 | ((code >> 12) & 0x3f);
                // 再留6位给低位
                byte5 = 0x80 | ((code >> 6) & 0x3f);
                // 10xxxxxx
                byte6 = 0x80 | (code & 0x3f);

                res.push(
                    String.fromCharCode(byte1),
                    String.fromCharCode(byte2),
                    String.fromCharCode(byte3),
                    String.fromCharCode(byte4),
                    String.fromCharCode(byte5),
                    String.fromCharCode(byte6)
                );
            }
        }

        return res.join('');
    },

    /**
     * UTF8编码字符串转为UTF16编码字符串
     * @param {String} str utf8编码的字符串
     * @return {String} utf16编码的字符串,可以直接被js用来打印
     */
    utf8ToUtf16(str) {
        if (!str) {
            return '';
        }
        // res是用来存放结果的字符数组,最终会转为字符串返回
        const res = [];
        const len = str.length;


        for (let i = 0; i < len; i++) {
            // 获得对应的unicode码
            const code = str.charCodeAt(i);
            let byte1;
            let byte2;
            let byte3;
            let byte4;
            let byte5;
            let byte6;
            let code2;
            let code3;
            let code4;
            let code5;
            let code6;
            let utf16;

            // 对第一个字节进行判断
            if (((code >> 7) & 0xff) === 0x0) {
                // 0xFF 255 11111111,代表只取前8位
                // 右移7位,如果是只剩下0了,代表这个是单字节
                // 单字节
                // 0xxxxxxx
                res.push(str.charAt(i));
            } else if (((code >> 5) & 0xff) === 0x6) {
                // 双字节 110开头
                // 110xxxxx 10xxxxxx
                // 需要用到下一个字节
                code2 = str.charCodeAt(++i);
                // 0x1F 31 00011111
                // 取到第一个字节的后5位,然后左移6位(这6位留给第二个字节的低6位)，由于js是number型,所以不必担心溢出
                byte1 = (code & 0x1f) << 6;
                // 0x3F 63  00111111
                byte2 = code2 & 0x3f;
                // 或运算,因为第一个字节第六位没有,第二个字节只有低6位,所以算是结合了
                utf16 = byte1 | byte2;

                res.push(String.fromCharCode(utf16));
            } else if (((code >> 4) & 0xff) === 0xe) {
                // 三字节 1110开头
                // 1110xxxx 10xxxxxx 10xxxxxx
                code2 = str.charCodeAt(++i);
                code3 = str.charCodeAt(++i);
                // 和00001111与后, 左移12位
                byte1 = (code & 0x0f) << 12;
                // 和00111111与后,左移6位
                byte2 = (code2 & 0x3f) << 6;
                // 和00111111与
                byte3 = code3 & 0x3f;
                utf16 = byte1 | byte2 | byte3;

                res.push(String.fromCharCode(utf16));
            } else if (((code >> 3) & 0xff) === 0x1e) {
                // 四字节 11110开头
                // 11110xxx 10xxxxxx 10xxxxxx 10xxxxxx
                code2 = str.charCodeAt(++i);
                code3 = str.charCodeAt(++i);
                code4 = str.charCodeAt(++i);
                // 和00000111与后, 左移18位
                byte1 = (code & 0x07) << 18;
                // 和00111111与后,左移12位
                byte2 = (code2 & 0x3f) << 12;
                // 和00111111与后,左移6位
                byte3 = (code3 & 0x3f) << 6;
                // 和00111111与
                byte4 = code4 & 0x3f;
                utf16 = byte1 | byte2 | byte3 | byte4;

                res.push(String.fromCharCode(utf16));
            } else if (((code >> 2) & 0xff) === 0x3e) {
                // 五字节 111110开头
                // 111110xx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
                code2 = str.charCodeAt(++i);
                code3 = str.charCodeAt(++i);
                code4 = str.charCodeAt(++i);
                code5 = str.charCodeAt(++i);
                // 和00000011与后, 左移24位
                byte1 = (code & 0x03) << 24;
                // 和00111111与后,左移18位
                byte2 = (code2 & 0x3f) << 18;
                // 和00111111与后,左移12位
                byte3 = (code3 & 0x3f) << 12;
                // 和00111111与后,左移6位
                byte4 = (code4 & 0x3f) << 6;
                // 和00111111与
                byte5 = code5 & 0x3f;
                utf16 = byte1 | byte2 | byte3 | byte4 | byte5;

                res.push(String.fromCharCode(utf16));
            } else {
                // if (((code >> 1) & 0xFF) == 0x7E)
                // 六字节 1111110开头
                // 1111110x 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
                code2 = str.charCodeAt(++i);
                code3 = str.charCodeAt(++i);
                code4 = str.charCodeAt(++i);
                code5 = str.charCodeAt(++i);
                code6 = str.charCodeAt(++i);
                // 和00000001与后, 左移30位
                byte1 = (code & 0x01) << 30;
                // 和00111111与后,左移24位
                byte2 = (code2 & 0x3f) << 24;
                // 和00111111与后,左移18位
                byte3 = (code3 & 0x3f) << 18;
                // 和00111111与后,左移12位
                byte4 = (code4 & 0x3f) << 12;
                // 和00111111与后,左移6位
                byte5 = (code5 & 0x3f) << 6;
                // 和00111111与
                byte6 = code6 & 0x3f;
                utf16 = byte1 | byte2 | byte3 | byte4 | byte5 | byte6;

                res.push(String.fromCharCode(utf16));
            }
        }

        return res.join('');
    },

    /**
     * Base64编码要求把3个8位字节（3*8=24）转化为4个6位的字节（4*6=24），
     * 之后在6位的前面补两个0，形成8位一个字节的形式。
     * 由于2的6次方为64， 所以每6个位为一个单元， 对应某个可打印字符。
     * 当原数据不是3的整数倍时， 如果最后剩下两个输入数据，
     * 在编码结果后加1个“=；如果最后剩下一个输入数据，编码结果后加2个“=；
     * 如果没有剩下任何数据，就什么都不要加，这样才可以保证资料还原的正确性。
     */
    base64: {
        /**
         * base64转码表,最后一个=号是专门用来补齐的
         */
        keyTable: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
        /**
         * 将一个目标字符串编码为base64字符串
         * @param {String} str 传入的目标字符串
         * 可以是任何编码类型,传入什么类型就输出成了什么样的编码
         * 由于js内置是utf16编码,而服务器端一般不使用这种,
         * 所以传入的编码一般是采取utf8或gbk的编码
         * @return {String} 编码后的base64字符串
         */
        _encode(str) {
            if (!str) {
                return '';
            }
            // 遍历索引
            const len = str.length;
            const { keyTable } = this;
            let i = 0;
            const res = [];
            let c1;
            let c2;
            let c3 = '';
            // 用来存对应的位置
            let enc1;
            let enc2;
            let enc3;
            let enc4 = '';

            while (i < len) {
                c1 = str.charCodeAt(i++) & 0xff;
                c2 = str.charCodeAt(i++);
                c3 = str.charCodeAt(i++);
                enc1 = c1 >> 2;
                enc2 = ((c1 & 0x3) << 4) | ((c2 >> 4) & 0x0f);
                enc3 = ((c2 & 0x0f) << 2) | ((c3 & 0xc0) >> 6);
                enc4 = c3 & 0x3f;
                // 专门用来补齐=号的
                if (isNaN(c2)) {
                    enc3 = 0x40;
                    enc4 = 0x40;
                } else if (isNaN(c3)) {
                    enc4 = 0x40;
                }
                res.push(keyTable.charAt(enc1));
                res.push(keyTable.charAt(enc2));
                res.push(keyTable.charAt(enc3));
                res.push(keyTable.charAt(enc4));
                c1 = '';
                c2 = '';
                c3 = '';
                enc1 = '';
                enc2 = '';
                enc3 = '';
                enc4 = '';
            }

            return res.join('');
        },
        /**
         * 解码base64字符串，还原为编码前的结果
         * @param {String} str 传入的目标字符串
         * 可以是任何编码类型,传入什么类型就输出成了什么样的编码
         * 由于js内置是utf16编码,而服务器端一般不使用这种,
         * 所以传入的编码一般是采取utf8或gbk的编码
         * @return {String} 编码后的base64字符串
         */
        _decode(str) {
            if (!str) {
                return '';
            }
            // 这里要判断目标字符串是不是base64型,如果不是,直接就不解码了
            // 两层判断
            if (str.length % 4 !== 0) {
                return '';
            }
            const base64test = /[^A-Za-z0-9+/=]/g;

            if (base64test.exec(str)) {
                return '';
            }
            const len = str.length;
            const { keyTable } = this;
            let i = 0;
            const res = [];
            let code1;
            let code2;
            let code3;
            let code4;
            let c1;
            let c2;
            let c3 = '';

            while (i < len) {
                code1 = keyTable.indexOf(str.charAt(i++));
                code2 = keyTable.indexOf(str.charAt(i++));
                code3 = keyTable.indexOf(str.charAt(i++));
                code4 = keyTable.indexOf(str.charAt(i++));

                c1 = (code1 << 2) | (code2 >> 4);
                c2 = ((code2 & 0xf) << 4) | (code3 >> 2);
                c3 = ((code3 & 0x3) << 6) | code4;

                res.push(String.fromCharCode(c1));

                if (code3 !== 64) {
                    res.push(String.fromCharCode(c2));
                }
                if (code4 !== 64) {
                    res.push(String.fromCharCode(c3));
                }
            }

            return res.join('');
        },
        /**
         * 将字符串进行base64编码，之后再进行uri编码
         * @param {String} str 传入的utf16编码
         * @param {String} type 类别，默认是utf8（去除gbk支持）
         * @param {Boolean} isUri 是否uri编码
         * @return {String} 编码后并uri编码的base64字符串
         */
        encode(str, type, isUri) {
            type = type || 'utf8';
            if (type === 'gbk') {
                console.error('gbk暂不支持');

                return str;
            } else if (type === 'utf8') {
                // 转成 utf8
                str = charset.utf16ToUtf8(str);
            }
            // 否则就是默认的utf16不要变
            // 先b64编码，再uri编码(防止网络传输出错)
            let b64Str = this._encode(str);

            if (isUri) {
                b64Str = encodeURIComponent(b64Str);
            }

            return b64Str;
        },
        /**
         * 将字符串先进行uri解码，再进行base64解码w
         * @param {String} str 传入的编码后的base64字符串
         * @param {String} type 类别，默认是utf8（去除gbk支持）
         * @param {Boolean} isUri 是否uri解码
         * @return {String} 编码后并uri编码的base64字符串
         */
        decode(str, type, isUri) {
            type = type || 'utf8';
            if (isUri) {
                str = decodeURIComponent(str);
            }
            const decodeStr = this._decode(str);

            if (type === 'gbk') {
                console.error('gbk暂不支持');

                return str;
            }
            if (type === 'utf8') {
                return charset.utf8ToUtf16(decodeStr);
            }

            // 否则就是默认的utf16不要变
            return decodeStr;
        }
    }
};

export default charset;
